// @ts-expect-error — opus-media-recorder não tem tipos
import OpusMediaRecorder from 'opus-media-recorder';
import { useEffect, useRef, useState } from 'react';
import { WAVE_BAR_CSS, WAVE_GAP_CSS, WAVE_RADIUS_CSS, shapeWaveAmp } from '../lib/waveform-style';

interface Props {
  onComplete: (blob: Blob, durationSec: number, waveform: number[]) => void | Promise<void>;
  onCancel: () => void;
}

/** Reduz todos os picos da gravação a N valores (0..1) — vira a waveform REAL
 *  salva na bolha (32 valores, igual aos áudios espelhados). Max por bin. */
function downsampleWaveform(peaks: number[], target: number): number[] {
  if (peaks.length === 0) return [];
  const out: number[] = [];
  for (let i = 0; i < target; i++) {
    const s = Math.floor((i * peaks.length) / target);
    const e = Math.max(s + 1, Math.floor(((i + 1) * peaks.length) / target));
    let mx = 0;
    for (let j = s; j < e && j < peaks.length; j++) {
      const v = peaks[j] ?? 0;
      if (v > mx) mx = v;
    }
    out.push(mx);
  }
  return out;
}

const MAX_SECONDS = 5 * 60; // limite Meta
const WARN_AT = 4 * 60 + 30; // alerta a 4:30
const BAR_COUNT = 200; // buffer rolante (desenha as que cabem, ~1 a cada 4px)

/**
 * Worker options pro OpusMediaRecorder. Os assets ficam em `apps/web/public/opus/`
 * (copiados do package `opus-media-recorder` no install).
 *
 * O OpusMediaRecorder é a polyfill MediaRecorder que encoda Opus em Ogg
 * direto no browser via Web Worker + WASM — produz OpusHead com `pre_skip=0`
 * e OpusTags vendor `opus-media-recorder` nativamente, sem precisar de
 * conversão server-side. Mesma stack usada pela web app `chatplus.atenderbem.com`.
 *
 * As `opusWorkerOptions` apontam o `encoderWorkerFactory` pro worker do encoder
 * em `/opus/encoderWorker.umd.js` (+ os caminhos dos WASM Ogg/WebM).
 */
interface OpusRecorder {
  state: 'inactive' | 'recording' | 'paused';
  mimeType: string;
  ondataavailable: ((e: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((e: ErrorEvent) => void) | null;
  start: (timeslice?: number) => void;
  stop: () => void;
}

/**
 * Story 4.5 — gravação de áudio inline.
 *
 * Usa `OpusMediaRecorder` (polyfill JS+WASM) no lugar do `MediaRecorder`
 * nativo. Browser nativo só grava webm/opus (Chrome) — Meta WhatsApp
 * Cloud rejeita pra áudios longos por causa do `pre_skip=312` que libopus
 * default escreve no OpusHead. O OpusMediaRecorder gera o ogg/opus com
 * `pre_skip=0` e vendor `opus-media-recorder` nativamente.
 *
 * UI: pílula larga substitui o composer (textarea+ícones) durante a gravação.
 * - Timer hh:mm:ss à esquerda
 * - Waveform em tempo real
 * - Botão stop (círculo vermelho) à direita, fora da pílula
 * - Auto-stop a 5min, aviso visual a 4:30
 */
export function AudioRecorder({ onComplete, onCancel }: Props) {
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<OpusRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const cancelRef = useRef(false);
  // started = 1º chunk encodado chegou (= WASM pronto). safeStop usa isso pra
  // decidir se chama rec.stop() (encoder ativo) ou só mata o mic (WASM ainda
  // carregando — stop() nesse ponto crasharia o worker).
  const startedRef = useRef(false);
  const durationRef = useRef(0);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Buffer rolante de amplitudes (0..1). Mais recente na direita.
  const ampsRef = useRef<number[]>(new Array(BAR_COUNT).fill(0));
  // Maior pico da gravação — usado pra NORMALIZAR a altura (igual prévia normalize:true).
  const peakMaxRef = useRef(0);
  // Todos os picos (sem cap) — viram os 32 valores reais salvos na bolha.
  const allPeaksRef = useRef<number[]>([]);
  // FIX: o effect de setup roda 1x (dep []); o onstop lê o onComplete mais recente
  // por ESTA ref, então re-render do Composer NÃO recria o gravador no meio (era a
  // causa do áudio cortado). stoppedRef torna o stop() idempotente.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const stoppedRef = useRef(false);

  // Para o gravador de forma SEGURA e idempotente. Se o encoder ainda não começou
  // (WASM carregando), NÃO chama stop() — ele postaria `done` num encoder undefined
  // e o worker crasha ("reading 'close'"); nesse caso só mata o mic e abandona.
  const safeStop = (): void => {
    const rec = recorderRef.current;
    if (!rec || stoppedRef.current || rec.state === 'inactive') return;
    stoppedRef.current = true;
    if (!startedRef.current) {
      for (const t of streamRef.current?.getTracks() ?? []) t.stop();
      return;
    }
    try {
      rec.stop();
    } catch {
      /* worker pode já ter sido finalizado */
    }
  };

  useEffect(() => {
    let cancelled = false;

    const start = async (): Promise<void> => {
      try {
        // AGC ligado (padrão do navegador): o usuário prefere o ganho automático
        // — começa mais alto e ajusta o nível ("padrão alto no começo"). Inclui
        // noiseSuppression + echoCancellation por padrão.
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          for (const t of stream.getTracks()) t.stop();
          return;
        }
        streamRef.current = stream;
        const opusWorkerOptions = {
          encoderWorkerFactory: () => new Worker('/opus/encoderWorker.umd.js'),
          OggOpusEncoderWasmPath: '/opus/OggOpusEncoder.wasm',
          WebMOpusEncoderWasmPath: '/opus/WebMOpusEncoder.wasm',
        };
        const rec = new OpusMediaRecorder(
          stream,
          {
            mimeType: 'audio/ogg;codecs=opus',
            audioBitsPerSecond: 32_000, // PTT WhatsApp ~24-32kbps
          },
          opusWorkerOptions,
        ) as OpusRecorder;
        recorderRef.current = rec;
        rec.ondataavailable = (e: { data: Blob }) => {
          startedRef.current = true; // 1º chunk = WASM carregado / encoder ativo
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        rec.onstop = () => {
          // Para o microfone AQUI — DEPOIS do flush do encoder. Matar a track
          // antes do stop() async terminar corta a cauda + a página final do OGG
          // (= o corte de áudio). Antes isso era feito no finishRecording, na hora.
          for (const t of streamRef.current?.getTracks() ?? []) t.stop();
          if (cancelRef.current) return;
          const blob = new Blob(chunksRef.current, { type: 'audio/ogg; codecs=opus' });
          chunksRef.current = [];
          void onCompleteRef.current(
            blob,
            durationRef.current,
            downsampleWaveform(allPeaksRef.current, 32),
          );
        };
        rec.onerror = () => {
          setError('Falha ao gravar o áudio. Tente novamente.');
        };
        rec.start(250); // chunks de 250ms

        // ── Waveform em tempo real via WebAudio ──
        const Ctor: typeof AudioContext =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new Ctor();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.4;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        sourceRef.current = source;
        analyserRef.current = analyser;

        const buf = new Uint8Array(analyser.fftSize);
        let lastSampleAt = 0;
        // ⚠️ THROTTLE a ~10fps (não a cada frame). A captura do opus-media-recorder
        // é via ScriptProcessorNode no MAIN-THREAD; ler o analyser + redesenhar o
        // canvas a 60fps saturava o main-thread e, no Chrome/Windows, o
        // ScriptProcessor perdia buffers → áudio cortado. O AtenderBem (mesma lib)
        // desenha ~7fps e não corta. Aqui gateamos TODO o trabalho (analyser+draw)
        // no intervalo — a ondinha segue igual, só atualiza menos vezes.
        const SAMPLE_INTERVAL_MS = 100; // ~10fps

        const tick = (now: number): void => {
          if (cancelled) return;
          if (now - lastSampleAt >= SAMPLE_INTERVAL_MS) {
            analyser.getByteTimeDomainData(buf);
            // PICO bruto (max |amostra-128|). A altura é NORMALIZADA no desenho
            // pelo maior pico da gravação (peakMaxRef) — igual ao WaveSurfer da
            // prévia (normalize:true): o trecho mais alto preenche a altura toda.
            let peak = 0;
            for (let i = 0; i < buf.length; i++) {
              const v = Math.abs(buf[i]! - 128) / 128;
              if (v > peak) peak = v;
            }
            if (peak > peakMaxRef.current) peakMaxRef.current = peak;
            allPeaksRef.current.push(peak);
            ampsRef.current.push(peak);
            if (ampsRef.current.length > BAR_COUNT) ampsRef.current.shift();
            drawWaveform(canvasRef.current, ampsRef.current, peakMaxRef.current);
            lastSampleAt = now;
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (err) {
        setError(
          err instanceof Error && err.name === 'NotAllowedError'
            ? 'Permissão de microfone negada. Habilite nas configurações do navegador.'
            : 'Não foi possível acessar o microfone.',
        );
      }
    };
    void start();

    return () => {
      cancelled = true;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      try {
        sourceRef.current?.disconnect();
      } catch {
        /* ignore */
      }
      try {
        analyserRef.current?.disconnect();
      } catch {
        /* ignore */
      }
      void audioCtxRef.current?.close();
      audioCtxRef.current = null;
      analyserRef.current = null;
      sourceRef.current = null;
      cancelRef.current = true;
      safeStop();
      for (const t of streamRef.current?.getTracks() ?? []) t.stop();
    };
    // FIX: dep [] — o gravador vive UMA montagem (do gravar ao parar). onComplete
    // é lido via onCompleteRef, então re-render do Composer não recria o gravador
    // no meio (era a causa do áudio cortado/mudo). safeStop é estável (só refs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds((s) => {
        const next = s + 1;
        durationRef.current = next;
        if (next >= MAX_SECONDS) {
          finishRecording();
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const finishRecording = (): void => {
    cancelRef.current = false;
    // safeStop só chama rec.stop() se o encoder já começou (senão descarta sem
    // crashar). O onstop para o microfone DEPOIS do flush (preserva a cauda do OGG).
    const wasStarted = startedRef.current;
    safeStop();
    if (!wasStarted) {
      // Parou ANTES do encoder iniciar (WASM ainda carregando): não há áudio e o
      // onstop não vai disparar → fecha a UI na mão (senão a pílula trava na tela).
      for (const t of streamRef.current?.getTracks() ?? []) t.stop();
      onCancel();
      return;
    }
    if (!recorderRef.current || recorderRef.current.state === 'inactive') {
      for (const t of streamRef.current?.getTracks() ?? []) t.stop();
    }
  };

  const cancel = (): void => {
    cancelRef.current = true;
    safeStop();
    for (const t of streamRef.current?.getTracks() ?? []) t.stop();
    onCancel();
  };

  if (error) {
    return (
      <div className="flex flex-1 items-center gap-2 rounded-pill bg-danger-50 px-4 py-2 text-[13px] text-danger-700">
        <span className="flex-1">{error}</span>
        <button
          type="button"
          onClick={onCancel}
          className="font-semibold underline"
          aria-label="Fechar"
        >
          OK
        </button>
      </div>
    );
  }

  const warning = seconds >= WARN_AT;
  const pillBg = warning ? 'bg-warning-50' : 'bg-n-100';
  const timerColor = warning ? 'text-warning-700' : 'text-n-900';

  return (
    <>
      <div
        className={`flex min-h-[44px] min-w-0 flex-1 items-center gap-3 rounded-pill px-4 py-2 ${pillBg}`}
      >
        <span className={`font-mono text-[13px] tabular-nums ${timerColor}`}>
          {formatTime(seconds)}
        </span>
        {/* min-w-0: um <canvas> é flex item com `min-width:auto` = largura
            intrínseca (o atributo width). drawWaveform seta width = clientWidth
            × devicePixelRatio; em telas com dpr>1 (escala Windows 125/150%,
            "melhor desempenho") isso faz a intrínseca > layout → o flex não
            encolhe → a pílula cresce e empurra o botão pra direita, num loop a
            cada frame. min-w-0 deixa o item encolher e quebra o feedback. */}
        <canvas ref={canvasRef} className="h-6 min-w-0 flex-1" aria-hidden="true" />
      </div>
      <button
        type="button"
        onClick={finishRecording}
        onContextMenu={(e) => {
          e.preventDefault();
          cancel();
        }}
        aria-label="Parar e enviar gravação (botão direito cancela)"
        title="Clique pra parar e enviar — clique direito pra cancelar"
        className="ml-1 flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-pill border-2 border-danger-500 bg-n-0 transition hover:bg-danger-50"
      >
        <span className="h-[10px] w-[10px] rounded-[2px] bg-danger-500" />
      </button>
    </>
  );
}

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function drawWaveform(canvas: HTMLCanvasElement | null, amps: number[], norm: number): void {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const cssWidth = canvas.clientWidth;
  const cssHeight = canvas.clientHeight;
  if (cssWidth === 0 || cssHeight === 0) return;

  const targetW = Math.round(cssWidth * dpr);
  const targetH = Math.round(cssHeight * dpr);
  if (canvas.width !== targetW) canvas.width = targetW;
  if (canvas.height !== targetH) canvas.height = targetH;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#94A3B8'; // n-400

  // Estilo compartilhado com a prévia (waveform-style.ts): barra/gap/cantos.
  const barW = Math.max(1, Math.round(WAVE_BAR_CSS * dpr));
  const gap = Math.max(1, Math.round(WAVE_GAP_CSS * dpr));
  const radius = Math.round(WAVE_RADIUS_CSS * dpr);
  const step = barW + gap;
  const minH = Math.max(1, Math.round(WAVE_BAR_CSS * dpr));
  const cx = canvas.height / 2;

  // Desenha as barras MAIS RECENTES que cabem (mais novas à direita).
  const fit = Math.max(1, Math.floor(canvas.width / step));
  const start = Math.max(0, amps.length - fit);
  const rr = (
    ctx as unknown as {
      roundRect?: (x: number, y: number, w: number, h: number, r: number) => void;
    }
  ).roundRect;
  for (let i = start; i < amps.length; i++) {
    const amp = shapeWaveAmp(amps[i] ?? 0, norm);
    const h = Math.max(minH, amp * canvas.height * 0.92);
    const x = Math.round((i - start) * step);
    const y = Math.round(cx - h / 2);
    const hh = Math.round(h);
    if (typeof rr === 'function') {
      ctx.beginPath();
      rr.call(ctx, x, y, barW, hh, radius);
      ctx.fill();
    } else {
      ctx.fillRect(x, y, barW, hh);
    }
  }
}
